import {Box, Typography} from "@mui/material";
import {connect} from "react-redux";
import {selectLocalization, selectLocalizedKeys} from "../../redux/selectors/firebase";
import {makeStyles} from "@mui/styles";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {FixedSizeList} from "react-window";
import {selectModeData} from "../../redux/selectors/editor";
import {ILocalizationData, UpdateModeData, updateModeData} from "../../redux/slices/editor";
import {saveAs} from "file-saver";
import {UpdateTextDialog} from "./UpdateTextDialog";
import {TextRow} from "./TextRow";
import {exportLocalizationStrings} from "../../utility/cookbookExporter";
import {LocalizedLanguages} from "./Localization";

const useStyles = makeStyles((theme) => ({
    content: {
        height: "100%",
        width: "100%"
    },
    primary: {
        marginRight: theme.spacing(2),
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: 300
    },
    date: {
        marginTop: theme.spacing(8),
        margin: theme.spacing(1),
        width: 90
    },
    english: {
        flexGrow: 0
    },
    id: {
        marginTop: theme.spacing(8),
        margin: theme.spacing(1),
        cursor: "pointer",
        width: 90
    },
    language: {
        paddingTop: theme.spacing(3),
        marginTop: theme.spacing(8),
        transform: "rotate(315deg)",
        whiteSpace: "nowrap",
        width: 30
    },
    languageLabel: {
        borderBottom: "2px solid lightgrey"
    },
    table: {
        marginRight: theme.spacing(10)
    },
    tableContent: {
        maxHeight: "100%",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    tableHead: {
    },
    text: {
        marginTop: theme.spacing(8),
        flexGrow: 1,
        margin: theme.spacing(1),
        width: 250
    },
    wrapped: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }
}));

type LocalizedKeys = {[key: string]: string};

export type LocalisedString = {[key: string]: ILocalisedString}
export type LocalizationData = {[key: string]: LocalisedString};

export interface ILocalisedString {
    text: string
    timestamp: number
}

interface ILocalization {
    localization: LocalizationData,
    localizationData: ILocalizationData
    localizedKeys: LocalizedKeys
    project: string
    updateModeData: UpdateModeData
}

function LocalizedText({localization, localizationData, localizedKeys, project, updateModeData}: ILocalization) {
    const classes = useStyles();
    const [editLocalizedKey, setEditLocalizedKey] = useState("");
    const [editLocalizedLanguage, setEditLocalizedLanguage] = useState("");
    const [localizedText, setLocalizedText] = useState("");
    const [paperBounds, setPaperBounds] = useState({height: 1000, width: 1000});
    const contentRef = useRef<HTMLDivElement>(null);
    const {set, track, untrack} = useContext(FirebaseContext);
    const handleLocalizationClick = useCallback((key: string, language: string) => {
        setLocalizedText(localization[key ?? ""][language ?? ""]?.text ?? "");
        setEditLocalizedKey(key ?? "");
        setEditLocalizedLanguage(language ?? "");
    }, [localization]);
    const handleEditLocalizedClose = () => setEditLocalizedKey("");
    const itemKeys = Object.keys(localizedKeys).filter(key => localization && localization[key]);
    const renderTableRow = useCallback(({index, style}) => {
        const key = itemKeys[index];

        return (
            <TextRow localizedKey={key} localization={localization[key]} onClick={handleLocalizationClick}
                     style={style}/>
        );
    }, [handleLocalizationClick, itemKeys, localization]);

    useEffect(() => {
        if (localizationData.exportJson && localization) {
            const blob = new Blob([JSON.stringify(exportLocalizationStrings(localization))], {
                type: "text/plain;charset=utf-8"
            });

            saveAs(blob, "loc_strings.json");
            updateModeData({exportJson: false});
        }
    }, [localization, localizationData, updateModeData])
    useEffect(() => {
        if (contentRef.current) {
            const {height, width} = contentRef.current.getBoundingClientRect();

            setPaperBounds({height, width});
        }
    }, [localization]);
    useEffect(() => {
        const keys = Object.keys(localizedKeys).filter(key => key !== "00000000000000000000000000");

        if (! localization) return;

        const update: LocalizationData = {};

        keys.forEach(key => {
            if (localization[key] === undefined || localization[key].english.text !== localizedKeys[key]) {
                update[key] = {
                    ...localization[key],
                    english: {
                        text: localizedKeys[key],
                        timestamp: Date.now()
                    }
                };
            }
        });
        const pruned = Object.fromEntries(Object.entries(localization).filter(([key]) => keys.includes(key)));
        if (Object.keys(pruned).length !== Object.keys(localization).length || Object.keys(update).length > 0) {
            set(`localization/${project}`, {...pruned, ...update}).catch(console.error);
        }
    }, [localizedKeys, localization, project, set]);
    useEffect(() => {
        track(`localization/${project}`, ["localization"]);
        return () => untrack(`localization/${project}`);
    }, [project, track, untrack]);
    if (! localization) return null;
    return (
        <Box className={classes.content} ref={contentRef}>
            <UpdateTextDialog editLocalizedKey={editLocalizedKey} englishText={localizedKeys[editLocalizedKey]}
                          language={editLocalizedLanguage} localizedText={localizedText}
                          onClose={handleEditLocalizedClose} project={project}/>
            <Box className={classes.table}>
                <Box className={classes.tableHead} display="flex">
                    <Box className={classes.id}><Typography><strong>Key</strong></Typography></Box>
                    <Box className={classes.text}><Typography><strong>Text</strong></Typography></Box>
                    <Box className={classes.date}><Typography><strong>Modified</strong></Typography></Box>
                    {Object.keys(LocalizedLanguages).map(language =>
                        <Box className={classes.language} key={language}>
                            <Typography>
                                <strong className={classes.languageLabel}>{LocalizedLanguages[language]}</strong>
                            </Typography>
                        </Box>
                    )}
                </Box>
                <FixedSizeList className={classes.tableContent} itemSize={40} height={paperBounds.height}
                               itemCount={itemKeys.length} width={paperBounds.width}>
                    {renderTableRow}
                </FixedSizeList>
            </Box>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    localization: selectLocalization(store),
    localizationData: selectModeData(store) as ILocalizationData,
    localizedKeys: selectLocalizedKeys(store) || {}
});

const connected = connect(mapStateToProps, {
    updateModeData
})(LocalizedText);
export {connected as LocalizedText};